import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { isApiError, isRateLimitError } from '@/shared/api';

/**
 * Turns a thrown error into a sentence the user can read.
 *
 * The rule this enforces: **branch on `errorCode`, never on message text.**
 * WalletFront rendered `data.error || data.message || 'Ошибка'` straight from the
 * response, which meant the backend was writing the UI copy, in one language,
 * with no review. Here the transport carries only machine-readable facts and the
 * lookup happens once, here.
 *
 * Resolution order:
 *   1. `errors.code.<errorCode>` — the specific, translated case.
 *   2. `errors.kind.<kind>`      — network / timeout / rate-limit / parse.
 *   3. `errors.unknown`          — genuine last resort.
 *
 * An unrecognised `errorCode` falls through to the kind rather than rendering
 * the raw key, so a backend that adds a code before the frontend does degrades
 * to "the service is unavailable" instead of printing `errors.code.foo_bar`.
 */
export function useApiErrorMessage(): (error: unknown) => string {
  const { t, i18n } = useTranslation();

  return useCallback(
    (error: unknown): string => {
      // The keys here are composed at runtime from a backend `errorCode`, so
      // they cannot be members of the statically-typed key union. Existence is
      // checked with `i18n.exists` before every lookup, which is the guarantee
      // the type would otherwise have given us.
      const lookup = t as unknown as (key: string) => string;

      if (!isApiError(error)) return t('errors.unknown');

      // errorCode BEFORE the rate-limit kind, deliberately. The backend answers
      // an account lockout with HTTP 429 + `account_locked`, so checking
      // rate-limit first would render "too many requests, wait 15s" for what is
      // actually a 15-MINUTE lockout of the account — wrong advice, wrong
      // duration, and it hides the security-relevant fact.
      if (error.errorCode) {
        const key = `errors.code.${error.errorCode}`;
        // `exists` rather than a try/catch on a missing key: i18next would
        // otherwise happily return the key itself as the "translation".
        if (i18n.exists(key)) return lookup(key);
      }

      if (isRateLimitError(error)) {
        return t('errors.kind.rate-limit', { seconds: error.retryAfterSeconds });
      }

      const kindKey = `errors.kind.${error.kind}`;
      if (i18n.exists(kindKey)) return lookup(kindKey);

      return t('errors.unknown');
    },
    [t, i18n]
  );
}

/** Reads the backend `errorCode` off a thrown error, or null. */
export function errorCodeOf(error: unknown): string | null {
  return isApiError(error) ? error.errorCode : null;
}
